import React, { useState, useRef, Children } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { FacebookIcon, InstagramIcon, YoutubeIcon, MailIcon, PhoneIcon, MapPinIcon, ArrowRightIcon, XIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { XLogoIcon } from './shared/Icons';
import { VisitorStats } from './VisitorStats';
export function Footer() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  const {
    t,
    language
  } = useLanguage();
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [accessibilityOpen, setAccessibilityOpen] = useState(false);
  const quickLinks = [{
    labelKey: 'footer.link.home',
    href: '#home'
  }, {
    labelKey: 'footer.link.mission',
    href: '#mission'
  }, {
    labelKey: 'footer.link.community',
    href: '#community'
  }, {
    labelKey: 'footer.link.hondurasFacts',
    href: '#community'
  }, {
    labelKey: 'footer.link.news',
    href: '#news'
  }, {
    labelKey: 'footer.link.leadership',
    href: '#leadership'
  }, {
    labelKey: 'footer.link.consular',
    href: '#consular-affairs'
  }, {
    labelKey: 'footer.link.exploreHonduras',
    href: '#explore-honduras'
  }, {
    labelKey: 'footer.link.businessInvestments',
    href: '#business-investments'
  }, {
    labelKey: 'footer.link.upliftingVoices',
    href: '#social-voices'
  }, {
    labelKey: 'footer.link.nonprofitDirectory',
    href: '#nonprofit-directory'
  }, {
    labelKey: 'footer.link.faq',
    href: '#faq'
  }];
  return <footer id="contact" className="bg-haa-navy" aria-label="Site footer" data-id="element-2309">
      {/* Smooth gradient into footer */}
      <div className="h-16 bg-gradient-to-b from-gray-50 to-haa-navy" data-id="element-2310" />

      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8" data-id="element-2311">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 mb-12" data-id="element-2312">
          {/* Column 1: About + Contact */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6
        }} data-id="element-2313">
            <div className="flex items-center gap-3 mb-5" data-id="element-2314">
              <img src="https://cdn.magicpatterns.com/uploads/b1GB8Z9aEvoE58p9BdX4qx/HonduranAmericanLogoNew.png" alt="Honduran American Association" className="h-14 w-auto object-contain brightness-0 invert" data-id="element-2315" />
            </div>

            <div className="flex items-center gap-3 mb-6" data-id="element-2316">
              {[{
              Icon: FacebookIcon,
              gradient: 'from-haa-blue to-blue-900',
              label: 'Facebook',
              href: '#'
            }, {
              Icon: InstagramIcon,
              gradient: 'from-haa-midblue to-haa-skyblue',
              label: 'Instagram',
              href: 'https://www.instagram.com/honduranamerican/'
            }, {
              Icon: XLogoIcon,
              gradient: 'from-haa-skyblue to-blue-400',
              label: 'X',
              href: 'https://x.com'
            }, {
              Icon: YoutubeIcon,
              gradient: 'from-haa-blue to-haa-navy',
              label: 'YouTube',
              href: '#'
            }].map(({
              Icon,
              gradient,
              label,
              href
            }) => <motion.a key={label} href={href} target={href !== '#' ? '_blank' : undefined} rel={href !== '#' ? 'noopener noreferrer' : undefined} aria-label={label} whileHover={{
              scale: 1.2,
              boxShadow: '0 0 15px rgba(74, 144, 217, 0.4)'
            }} whileTap={{
              scale: 0.9
            }} className={`w-9 h-9 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center shadow-lg`} data-id="element-2317">
                  <Icon className="w-4 h-4 text-white" data-id="element-2318" />
                </motion.a>)}
            </div>

            {/* Contact Us - below social icons */}
            <h3 className="font-bebas text-haa-skyblue text-2xl tracking-widest mb-4" data-id="element-2319">
              {t('footer.contact')}
            </h3>
            <ul className="space-y-3" data-id="element-2320">
              <li className="flex items-start gap-3" data-id="element-2321">
                <MapPinIcon className="w-4 h-4 text-haa-skyblue flex-shrink-0 mt-0.5" data-id="element-2322" />
                <span className="font-nunito text-white/60 text-sm" data-id="element-2323">
                  Washington D.C.
                </span>
              </li>
              <li className="flex items-center gap-3" data-id="element-2324">
                <PhoneIcon className="w-4 h-4 text-haa-skyblue flex-shrink-0" data-id="element-2325" />
                <a href="tel:+17033131465" className="font-nunito text-white/60 hover:text-white text-sm transition-colors" data-id="element-2326">
                  +1-703-313-1465
                </a>
              </li>
              <li className="flex items-center gap-3" data-id="element-2327">
                <svg className="w-4 h-4 text-haa-skyblue flex-shrink-0" viewBox="0 0 24 24" fill="currentColor" data-id="element-2328">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" data-id="element-2329" />
                </svg>
                <a href="https://wa.me/17033131465" target="_blank" rel="noopener noreferrer" className="font-nunito text-white/60 hover:text-white text-sm transition-colors" data-id="element-2330">
                  {language === 'ES' ? 'Enviar mensaje por WhatsApp' : 'Message us on WhatsApp'}
                </a>
              </li>
              <li className="flex items-center gap-3" data-id="element-2331">
                <MailIcon className="w-4 h-4 text-haa-skyblue flex-shrink-0" data-id="element-2332" />
                <a href="mailto:info@honduranamerican.org" className="font-nunito text-white/60 hover:text-white text-sm transition-colors" data-id="element-2333">
                  info@honduranamerican.org
                </a>
              </li>
            </ul>
          </motion.div>

          {/* Column 2: Quick Links */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.1
        }} data-id="element-2334">
            <h3 className="font-bebas text-haa-skyblue text-2xl tracking-widest mb-5" data-id="element-2335">
              {t('footer.quickLinks')}
            </h3>
            <ul className="space-y-2.5" data-id="element-2336">
              {quickLinks.map(link => <li key={link.labelKey} data-id="element-2337">
                  <a href={link.href} className="font-nunito text-white/60 hover:text-haa-skyblue text-sm flex items-center gap-2 group transition-colors" data-id="element-2338">
                    <ArrowRightIcon className="w-3.5 h-3.5 text-haa-blue group-hover:text-haa-skyblue transition-colors flex-shrink-0" data-id="element-2339" />
                    {t(link.labelKey)}
                  </a>
                </li>)}
            </ul>
          </motion.div>
        </div>

        {/* Divider */}
        <div className="h-px w-full bg-gradient-to-r from-transparent via-haa-skyblue/20 to-transparent mb-8" data-id="element-2340" />

        {/* Visitor Stats */}
        <VisitorStats data-id="element-2341" />

        {/* Divider */}
        <div className="h-px w-full bg-gradient-to-r from-transparent via-haa-skyblue/20 to-transparent mb-8 mt-8" data-id="element-2342" />

        {/* Bottom Bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-2343">
          <p className="font-nunito text-white/40 text-sm text-center sm:text-left" data-id="element-2344">
            {t('footer.copyright')}
          </p>
          <div className="flex items-center gap-4" data-id="element-2345">
            <button onClick={() => setPrivacyOpen(true)} className="font-nunito text-white/40 hover:text-white/70 text-sm transition-colors cursor-pointer bg-transparent border-none" data-id="element-2346">
              {t('footer.privacy')}
            </button>
            <span className="text-white/20" data-id="element-2347">·</span>
            <button onClick={() => setAccessibilityOpen(true)} className="font-nunito text-white/40 hover:text-white/70 text-sm transition-colors cursor-pointer bg-transparent border-none" data-id="element-2348">
              {t('footer.accessibility')}
            </button>
          </div>
        </div>
      </div>

      {/* Privacy Policy Modal */}
      <AnimatePresence data-id="element-2349">
        {privacyOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setPrivacyOpen(false)} data-id="element-2350">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3,
          ease: [0.16, 1, 0.3, 1]
        }} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-2351">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100" data-id="element-2352">
                <div className="flex items-center gap-3" data-id="element-2353">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-haa-blue to-haa-navy flex items-center justify-center shadow-sm" data-id="element-2354">
                    <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-2355">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" data-id="element-2356" />
                    </svg>
                  </div>
                  <h2 className="font-bebas text-3xl text-haa-navy tracking-wider" data-id="element-2357">
                    {language === 'ES' ? 'Política de Privacidad' : 'Privacy Policy'}
                  </h2>
                </div>
                <button onClick={() => setPrivacyOpen(false)} className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600" aria-label="Close" data-id="element-2358">
                  <XIcon className="w-5 h-5" data-id="element-2359" />
                </button>
              </div>

              {/* Content */}
              <div className="overflow-y-auto px-6 py-6 space-y-4" data-id="element-2360">
                {language === 'ES' ? <>
                    <p className="font-nunito text-gray-500 text-xs" data-id="element-2361">
                      Fecha de Vigencia: 1 de marzo de 2026
                      <br data-id="element-2362" />
                      Última Actualización: 25 de marzo de 2026
                    </p>

                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2363">
                      La Asociación Hondureño-Americana ("HAA," "la Asociación,"
                      "nosotros" o "nuestro") respeta su privacidad y se
                      compromete a manejar la información personal de manera
                      responsable, transparente y de acuerdo con la ley
                      aplicable.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2364">
                      1. Alcance
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2365">
                      Esta política se aplica a la información que recopilamos
                      en línea y fuera de línea a través de nuestro sitio web,
                      chatbot, formularios, comunicaciones, directorios y
                      servicios relacionados.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2366">
                      2. Información Que Recopilamos
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2367">
                      <strong data-id="element-2368">A. Información Que Usted Proporciona:</strong>{' '}
                      Nombre, correo electrónico, teléfono, dirección,
                      organización, mensajes y detalles de eventos o
                      suscripciones.
                    </p>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2369">
                      <strong data-id="element-2370">B. Chatbot y Mensajería:</strong> Nombre,
                      teléfono, contenido de mensajes, historial de
                      conversaciones, metadatos y datos técnicos para mejorar el
                      rendimiento.
                    </p>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2371">
                      <strong data-id="element-2372">C. Información Automática:</strong> Dirección IP,
                      tipo de navegador, sistema operativo, páginas visitadas,
                      cookies y patrones de uso.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2373">
                      3. Información Sensible
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2374">
                      No envíe información sensible a menos que sea
                      específicamente solicitada y necesaria.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2375">
                      4. Cómo Usamos la Información
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2376">
                      Usamos la información para responder consultas,
                      proporcionar recursos, mejorar servicios, comunicar
                      actualizaciones, gestionar programas, detectar fraude y
                      cumplir con obligaciones legales.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2377">
                      5. Bases Legales y Consentimiento
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2378">
                      Al usar nuestros servicios o proporcionar información,
                      usted consiente su uso según lo descrito. Cuando sea
                      requerido, obtendremos consentimiento explícito.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2379">
                      6. Cookies y Seguimiento
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2380">
                      Podemos usar cookies y herramientas de análisis. Puede
                      controlar las cookies a través de la configuración de su
                      navegador.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2381">
                      7. Aviso de Chatbot e IA
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2382">
                      Las conversaciones pueden ser grabadas y revisadas. El
                      chatbot es solo para fines informativos y no proporciona
                      asesoramiento legal, de inmigración o profesional. No
                      envíe información confidencial.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2383">
                      8. Divulgación de Información
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2384">
                      No vendemos información personal. Podemos compartir datos
                      con proveedores de servicios, autoridades legales y socios
                      organizacionales cuando sea necesario.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2385">
                      9. No Venta de Información Personal
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2386">
                      No vendemos ni alquilamos información personal.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2387">
                      10. Directorio de Negocios
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2388">
                      Los listados son solo informativos. La información puede
                      provenir de fuentes públicas. No se garantiza la
                      precisión. Los listados no son respaldos. No somos
                      responsables de daños relacionados con los listados.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2389">
                      11. Sitios Web de Terceros
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2390">
                      No somos responsables de sitios o servicios de terceros
                      vinculados en nuestro sitio web.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2391">
                      12. Retención de Datos
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2392">
                      Retenemos datos el tiempo necesario para operaciones,
                      cumplimiento legal y mejora del servicio.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2393">
                      13. Seguridad de Datos
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2394">
                      Usamos salvaguardas razonables, pero ningún sistema es
                      completamente seguro.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2395">
                      14. Privacidad de Niños
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2396">
                      No recopilamos datos de niños menores de 13 años a
                      sabiendas.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2397">
                      15. Sus Derechos de Privacidad
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2398">
                      Dependiendo de su ubicación, puede tener derechos para
                      acceder, corregir, eliminar o optar por no usar ciertos
                      usos de sus datos.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2399">
                      16. Cómo Ejercer Sus Derechos
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2400">
                      Para enviar una solicitud de privacidad, contáctenos en:
                      info@honduranamerican.org. Incluya suficientes detalles
                      para procesar su solicitud. Podemos verificar su identidad
                      antes de responder.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2401">
                      17. No Rastrear
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2402">
                      Es posible que no respondamos a todas las señales "No
                      Rastrear" del navegador, excepto según lo requiera la ley.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2403">
                      18. Transferencias de Datos
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2404">
                      Su información puede ser procesada en los Estados Unidos u
                      otras jurisdicciones.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2405">
                      19. Cambios a Esta Política
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2406">
                      Podemos actualizar esta política en cualquier momento. El
                      uso continuado indica aceptación.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2407">
                      20. Contáctenos
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2408">
                      Asociación Hondureño-Americana
                      <br data-id="element-2409" />
                      Sitio web: honduranamerican.org
                      <br data-id="element-2410" />
                      Correo: info@honduranamerican.org
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2411">
                      Aviso de Privacidad del Chatbot
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2412">
                      Este chatbot se proporciona solo con fines informativos
                      generales. Las conversaciones pueden ser grabadas,
                      almacenadas y revisadas para mejorar la calidad,
                      precisión, seguridad y rendimiento del servicio. No envíe
                      información personal sensible, financiera, médica u otra
                      información confidencial a través de este chatbot.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2413">
                      Descargo de Responsabilidad del Directorio
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2414">
                      Los listados de negocios y comunidades se proporcionan
                      solo con fines informativos. La información puede provenir
                      de fuentes públicas o de terceros y puede cambiar sin
                      previo aviso. La Asociación Hondureño-Americana no
                      verifica, respalda, certifica ni garantiza los negocios,
                      organizaciones, direcciones o servicios listados.
                    </p>
                  </> : <>
                    <p className="font-nunito text-gray-500 text-xs" data-id="element-2415">
                      Effective Date: March 1, 2026
                      <br data-id="element-2416" />
                      Last Updated: March 25, 2026
                    </p>

                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2417">
                      The Honduran-American Association ("HAA," "the
                      Association," "we," "us," or "our") respects your privacy
                      and is committed to handling personal information
                      responsibly, transparently, and in accordance with
                      applicable law.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2418">
                      1. Scope of This Privacy Policy
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2419">
                      This Privacy Policy applies to personal information we
                      collect online and offline in connection with our website,
                      chatbot, forms, communications, directory features, and
                      other services or outreach activities.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2420">
                      2. Information We Collect
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2421">
                      <strong data-id="element-2422">A. Information You Provide Directly:</strong>{' '}
                      Name, email address, phone number, mailing address,
                      organization name, messages, event registration details,
                      and volunteer or business inquiry information.
                    </p>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2423">
                      <strong data-id="element-2424">B. Chatbot and Messaging Information:</strong>{' '}
                      Name, phone number, message contents, conversation
                      history, timestamps, session details, and technical
                      information for performance improvement.
                    </p>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2425">
                      <strong data-id="element-2426">C. Information Collected Automatically:</strong>{' '}
                      IP address, browser type, operating system, device type,
                      pages visited, links clicked, usage patterns, and cookie
                      data.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2427">
                      3. Categories of Sensitive Information
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2428">
                      Please do not send sensitive personal information unless
                      specifically requested and necessary.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2429">
                      4. How We Use Information
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2430">
                      We use information to respond to inquiries, provide
                      community resources, operate and improve services, enhance
                      chatbot performance, communicate updates, manage programs,
                      detect fraud, and comply with legal obligations.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2431">
                      5. Legal Bases and Consent
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2432">
                      By using our services or providing information, you
                      consent to the use as described. We will obtain explicit
                      consent when required.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2433">
                      6. Cookies and Tracking
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2434">
                      We may use cookies and analytics tools to improve
                      performance and user experience. You can control cookies
                      through your browser settings.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2435">
                      7. Chatbot, AI, and Automated Interaction Notice
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2436">
                      Our website may use chatbot or AI tools. Conversations may
                      be recorded and reviewed. Responses may be inaccurate. The
                      chatbot is for informational purposes only and does not
                      provide legal, immigration, financial, or professional
                      advice. Do not submit sensitive or confidential
                      information.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2437">
                      8. How We Disclose Information
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2438">
                      We do not sell personal information. We may share data
                      with service providers (hosting, chatbot, analytics,
                      messaging), legal authorities if required, security
                      entities, and organizational partners when necessary.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2439">
                      9. No Sale of Personal Information
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2440">
                      We do not sell or rent personal information.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2441">
                      10. Business Directory & Third-Party Listings Disclaimer
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2442">
                      Our website may include listings of businesses and
                      organizations. Information may come from public or
                      third-party sources. Accuracy is not guaranteed. Listings
                      are not endorsements or certifications. Users must verify
                      information independently. We are not liable for damages,
                      disputes, or losses related to listings.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2443">
                      11. Third-Party Websites
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2444">
                      We are not responsible for third-party sites or services
                      linked on our website.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2445">
                      12. Data Retention
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2446">
                      We retain data for the necessary period for operations,
                      legal compliance, and service improvement.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2447">
                      13. Data Security
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2448">
                      We use reasonable safeguards, but no system is fully
                      secure. Use caution when sharing information.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2449">
                      14. Children's Privacy
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2450">
                      We do not knowingly collect data from children under 13.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2451">
                      15. Your Privacy Rights
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2452">
                      Depending on your location, you may have rights to access,
                      correct, delete your data, or opt out of certain uses.
                      Requests may be subject to verification and legal
                      limitations.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2453">
                      16. How to Exercise Your Rights
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2454">
                      To submit a privacy request, contact us at:
                      info@honduranamerican.org. Include sufficient details to
                      process your request. We may verify your identity before
                      responding.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2455">
                      17. Do Not Track
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2456">
                      We may not respond to all "Do Not Track" signals from your
                      browser, except as required by law.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2457">
                      18. Data Transfers
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2458">
                      Your information may be processed in the United States or
                      other jurisdictions.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2459">
                      19. Changes to This Policy
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2460">
                      We may update this policy at any time. Continued use of
                      our services indicates acceptance of updates.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2461">
                      20. Contact Us
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2462">
                      Honduran-American Association
                      <br data-id="element-2463" />
                      Website: honduranamerican.org
                      <br data-id="element-2464" />
                      Email: info@honduranamerican.org
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2465">
                      Chatbot Privacy Notice
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2466">
                      This chatbot is provided solely for general informational
                      purposes. Conversations may be recorded, stored, and
                      reviewed to improve quality, accuracy, security, and
                      performance. Do not send sensitive personal, financial,
                      medical, or other confidential information through this
                      chatbot.
                    </p>

                    <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2467">
                      Directory Disclaimer
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2468">
                      Business and community listings are provided solely for
                      informational purposes. Information may come from public
                      or third-party sources and may change without prior
                      notice. The Honduran-American Association does not verify,
                      endorse, certify, or guarantee any businesses,
                      organizations, addresses, or services listed.
                    </p>
                  </>}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50" data-id="element-2469">
                <button onClick={() => setPrivacyOpen(false)} className="w-full py-2.5 bg-haa-navy hover:bg-haa-blue text-white font-nunito font-bold text-sm rounded-xl transition-colors" data-id="element-2470">
                  {language === 'ES' ? 'Cerrar' : 'Close'}
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Accessibility Statement Modal */}
      <AnimatePresence data-id="element-2471">
        {accessibilityOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setAccessibilityOpen(false)} data-id="element-2472">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3,
          ease: [0.16, 1, 0.3, 1]
        }} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-2473">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100" data-id="element-2474">
                <div className="flex items-center gap-3" data-id="element-2475">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-haa-blue to-haa-navy flex items-center justify-center shadow-sm" data-id="element-2476">
                    <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-2477">
                      <circle cx="12" cy="4.5" r="2.5" data-id="element-2478" />
                      <path d="M12 7v5" data-id="element-2479" />
                      <path d="M8 22l4-10 4 10" data-id="element-2480" />
                      <path d="M6 12h12" data-id="element-2481" />
                    </svg>
                  </div>
                  <h2 className="font-bebas text-3xl text-haa-navy tracking-wider" data-id="element-2482">
                    {language === 'ES' ? 'Declaración de Accesibilidad' : 'Accessibility Statement'}
                  </h2>
                </div>
                <button onClick={() => setAccessibilityOpen(false)} className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600" aria-label="Close" data-id="element-2483">
                  <XIcon className="w-5 h-5" data-id="element-2484" />
                </button>
              </div>

              {/* Content */}
              <div className="overflow-y-auto px-6 py-6 space-y-4" data-id="element-2485">
                {language === 'ES' ? <>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2486">
                      La Asociación Hondureño-Americana se compromete a hacer
                      que este sitio web sea accesible y utilizable para la
                      mayor cantidad de personas posible, incluidas las personas
                      con discapacidades. Creemos que todos deberían poder
                      acceder a la información sobre nuestra misión, programas y
                      recursos con dignidad y facilidad.
                    </p>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2487">
                      Trabajamos continuamente para mejorar la accesibilidad y
                      usabilidad de este sitio y seguir las mejores prácticas
                      reconocidas para la accesibilidad digital. Como parte de
                      ese compromiso, nos esforzamos por diseñar contenido que
                      sea claro, legible y accesible en todos los dispositivos y
                      tecnologías de asistencia.
                    </p>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2488">
                      A pesar de nuestros esfuerzos, algunas áreas del sitio web
                      pueden no ser completamente accesibles o pueden estar aún
                      en proceso de mejora. Agradecemos los comentarios de los
                      visitantes que encuentren barreras al usar nuestro sitio.
                    </p>
                    <div className="mt-2 p-4 bg-blue-50 rounded-xl border border-blue-100" data-id="element-2489">
                      <p className="font-nunito text-gray-700 text-sm leading-relaxed" data-id="element-2490">
                        Si tiene dificultades para acceder a cualquier parte de
                        este sitio web, nota un problema de accesibilidad o
                        desea solicitar asistencia, contáctenos en{' '}
                        <a href="mailto:info@honduranamerican.org" className="text-haa-blue hover:text-haa-navy font-semibold underline transition-colors" data-id="element-2491">
                          info@honduranamerican.org
                        </a>
                        . Haremos todo lo posible para responder con prontitud y
                        brindarle la información o el apoyo que necesite.
                      </p>
                    </div>
                  </> : <>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2492">
                      The Honduran-American Association is committed to making
                      this website accessible and usable for as many people as
                      possible, including people with disabilities. We believe
                      everyone should be able to access information about our
                      mission, programs, and resources with dignity and ease.
                    </p>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2493">
                      We are continually working to improve the accessibility
                      and usability of this site and to follow recognized best
                      practices for digital accessibility. As part of that
                      commitment, we aim to design content that is clear,
                      readable, and accessible across devices and assistive
                      technologies.
                    </p>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2494">
                      Despite our efforts, some areas of the website may not yet
                      be fully accessible or may still be in the process of
                      improvement. We welcome feedback from visitors who
                      encounter barriers while using our site.
                    </p>
                    <div className="mt-2 p-4 bg-blue-50 rounded-xl border border-blue-100" data-id="element-2495">
                      <p className="font-nunito text-gray-700 text-sm leading-relaxed" data-id="element-2496">
                        If you experience difficulty accessing any part of this
                        website, notice an accessibility issue, or would like to
                        request assistance, please contact us at{' '}
                        <a href="mailto:info@honduranamerican.org" className="text-haa-blue hover:text-haa-navy font-semibold underline transition-colors" data-id="element-2497">
                          info@honduranamerican.org
                        </a>
                        . We will do our best to respond promptly and provide
                        the information or support you need.
                      </p>
                    </div>
                  </>}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50" data-id="element-2498">
                <button onClick={() => setAccessibilityOpen(false)} className="w-full py-2.5 bg-haa-navy hover:bg-haa-blue text-white font-nunito font-bold text-sm rounded-xl transition-colors" data-id="element-2499">
                  {language === 'ES' ? 'Cerrar' : 'Close'}
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </footer>;
}